import urijs from 'urijs';
import uuidv4 from 'uuid/v4';
import he from 'he';

/**
 * Replace placeholders on a URL. If one of more of the placeholders are not present on the URL,
 * the method will dynamically add them as query string arguments.
 *
 * @example
 * console.log(formatURL('http://{server}/login', { server: 'test.com' }));
 * // http://test.com/login
 *
 * console.log(formatURL('http://{server}/login', { server: 'test.com', version: '2.0' }));
 * // http://test.com/login?version=2.0
 *
 * @param {String} url        The base URL where the function will replace the placeholders.
 * @param {Object} parameters A dictionary with the placeholders to replace.
 * @return {String}
 */
export function formatURL(url, parameters) {
  const optionals = [];
  Object.keys(parameters).forEach((key) => {
    const placeholder = `{${key}}`;
    if (url.includes(placeholder)) {
      url = url.replace(placeholder, parameters[key]);
    } else {
      optionals.push(key);
    }
  });

  if (optionals.length) {
    const uriObj = urijs(url);
    optionals.forEach((key) => {
      uriObj.addQuery(key, parameters[key]);
    });

    url = uriObj.toString();
  }

  return url;
}

/**
 * It will redirect to the defined url.
 * @param {String} url    The route path.
 * @param {String} target The target where the url will be opened. Default value: `_self`.
 */
export function redirectToUrl(url, target = '_self') {
  window.open(url, target);
}

/**
 * Returns a v4 UUID (random).
 * @return {String} `416ac246-e7ac-49ff-93b4-f7e94d997e6b`
 */
export function getUUID() {
  return uuidv4();
}

/**
 * Makes a given promise cancelable. The returned promise has a `.cancel()` function
 * that will stop the `promise` param. Will rejected with `{ canceled: true }`.
 * ```
 * makePromiseCancelable(originalPromise).then(() => {
 *      // fulfilled
 * }).catch((rejectReason) => {
 *      if (rejectReason.canceled) {
 *          // ...
 *      }
 * });
 * ```
 * @param {Promise} promise The promise you want to cancel.
 * @return {Promise}
 */
export function makePromiseCancelable(promise) {
  // Extracted reject fn from the cancelable promise, in order to control it from outside
  let cancelFn;
  // Promise that will be rejected whenever `cancelFn` is called
  const cancelPromise = new Promise((resolve, reject) => {
    cancelFn = reject.bind(null, { canceled: true });
  });
  /*
   * Composed promise with the cancelable promise, the original promise and the `cancelFn`.
   * When `cancelablePromise.cancel()` gets called, thanks to `Promise.race` and
   * if the actual promise hasn't finished yet, cancelPromise will be rejected and the whole thing
   * will go down that path.
   */
  return Object.assign(Promise.race([promise, cancelPromise]), {
    cancel: cancelFn,
  });
}

/**
 * Format price given a price value and a currency format data.
 *
 * @example
 * console.log(formatPrice(
 *   1223223.45,
 *   { currency_symbol: '&#165;', thousand: ',', decimal: '.', precedes: 1, space: 0, rounding: 1 }
 * ));
 * // ￥1,223,223
 *
 * console.log(formatPrice(
 *   122322345.2,
 *   { currency_symbol: '$', thousand: ',', decimal: '.', precedes: 1, space: 0, rounding: 0 }
 * ));
 * // $122,322,345.20
 *
 * console.log(formatPrice(
 *   122322345.254,
 *   { currency_symbol: '&euro;', thousand: '.', decimal: ',', precedes: 1, space: 1, rounding: 0 }
 * ));
 * // € 122.322.345,25
 *
 * @param {Number} price              Price that will be formatted.
 * @param {Object} currencyFormat     The currency format data.
 * @property {String} currency_symbol The currency symbol for the price.
 * @property {String} decimal         It defines what is the symbol that separates the price with the cents value.
 * @property {Number} precedes        It defines if the currency symbol precedes or not the price value.
 * @property {Number} rounding        It defines if the price value should be rounded or not.
 * @property {Number} space           It defines if there is a space between the price value and the currency symbol.
 * @property {String} thousand        It defines what is the symbol that separates the thousand group of values.
 * @return {String}
 */
export function formatPrice(price, currencyFormat) {
  // Get cents value from the price.
  let cents = price.toFixed(2).split('.')[1];
  // It saves spacing information for the price format.
  const space = currencyFormat.space ? ' ' : '';
  // If rounding is needed, get rounded price. If not, get price without cents.
  const resultPriceInt = currencyFormat.rounding ? Math.round(price) : parseInt(price, 10);
  // Replace every 3 spaces with the thousand symbol. ie. (1200000 -> 1,200,000)
  let resultPriceString = resultPriceInt.toString().replace(/\B(?=(\d{3})+(?!\d))/g, currencyFormat.thousand);

  // Add cents to the price if rounding is defined.
  if (!currencyFormat.rounding) {
    if (!cents) {
      // If the price should not be rounded and the cents were not defined, add `00`.
      cents = '00';
    } else if (cents.length === 1) {
      // If the cents length is equal 1, add add a 0. ie. (12.9 -> 12.90).
      cents = `${cents}0`;
    }

    resultPriceString = `${resultPriceString}${currencyFormat.decimal}${cents}`;
  }

  // It adds the currency symbol before or after the price with or without the space.
  if (currencyFormat.precedes) {
    resultPriceString = `${currencyFormat.currency_symbol}${space}${resultPriceString}`;
  } else {
    resultPriceString = `${resultPriceString}${space}${currencyFormat.currency_symbol}`;
  }

  // Price needs to be decoded because of the currency symbol. ie. (&#165;330021 -> ¥330021)
  return he.decode(resultPriceString);
}



// WEBPACK FOOTER //
// ./src/services/AppUtils.js